import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';

/**
 * Entra ID sign-in (§70 #1).
 *
 * Configuration is fetched from the API at boot rather than compiled in, so the
 * one web image runs in every environment — see `GET /auth/config`. In dev mode
 * the API reports `mode: 'dev'` and none of this runs; the user switcher does
 * the job instead.
 *
 * Redirect rather than popup: popups are blocked by default in enough browsers,
 * and silently, that a login which sometimes does nothing is worse than one
 * extra page load.
 */

export interface DevAuthConfig {
  mode: 'dev';
}
export interface OidcAuthConfig {
  mode: 'oidc';
  clientId: string;
  authority: string;
  scopes: string[];
}
export type AuthConfig = DevAuthConfig | OidcAuthConfig;

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const response = await fetch('/api/v1/auth/config', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Could not read auth configuration (${response.status}).`);
  return (await response.json()) as AuthConfig;
}

let client: PublicClientApplication | null = null;
let config: OidcAuthConfig | null = null;

/** Initialises MSAL and completes any redirect we are returning from. */
export async function initialiseOidc(settings: OidcAuthConfig): Promise<AccountInfo | null> {
  config = settings;
  client = new PublicClientApplication({
    auth: {
      clientId: settings.clientId,
      authority: settings.authority,
      redirectUri: window.location.origin,
    },
    cache: {
      // sessionStorage would drop the session on every new tab, which for an
      // internal tool people keep open across a day reads as being logged out
      // at random.
      cacheLocation: 'localStorage',
    },
  });

  await client.initialize();

  // Must run before anything else touches the account list: this is what turns
  // the fragment Entra redirected back with into a usable session.
  const redirect = await client.handleRedirectPromise();
  if (redirect?.account) {
    client.setActiveAccount(redirect.account);
    return redirect.account;
  }

  const existing = client.getAllAccounts();
  if (existing.length > 0) {
    client.setActiveAccount(existing[0]);
    return existing[0];
  }
  return null;
}

export async function signIn(): Promise<void> {
  if (!client || !config) throw new Error('Sign-in was attempted before initialisation.');
  await client.loginRedirect({ scopes: config.scopes });
}

export async function signOut(): Promise<void> {
  if (!client) return;
  await client.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
}

export function activeAccount(): AccountInfo | null {
  return client?.getActiveAccount() ?? null;
}

/**
 * A bearer token for the API, refreshed silently when it can be.
 *
 * MSAL caches and renews on its own, so this is called per request rather than
 * held: asking each time is what keeps a long-open tab from failing on an
 * expired token.
 */
export async function accessToken(): Promise<string | null> {
  if (!client || !config) return null;
  const account = client.getActiveAccount();
  if (!account) return null;

  try {
    const result = await client.acquireTokenSilent({ account, scopes: config.scopes });
    return result.accessToken;
  } catch (error) {
    // The refresh token has expired or the tenant now wants consent or MFA —
    // none of which can be resolved without the user, so hand it back to them.
    // The same goes for a silent renewal that could not complete: once the SPA
    // refresh token (24h) lapses MSAL renews in a hidden iframe, and anything
    // that stops that iframe (a framing header, a blocked third-party cookie)
    // surfaces as a timeout. A full redirect still works, so use it rather than
    // showing an error the user cannot act on.
    if (error instanceof InteractionRequiredAuthError || isSilentRenewalFailure(error)) {
      await client.acquireTokenRedirect({ account, scopes: config.scopes });
      return null;
    }
    throw error;
  }
}

const SILENT_RENEWAL_FAILURES: ReadonlySet<string> = new Set([
  BrowserAuthErrorCodes.timedOut,
  BrowserAuthErrorCodes.blockIframeReload,
  BrowserAuthErrorCodes.iframeClosedPrematurely,
  BrowserAuthErrorCodes.emptyWindowError,
]);

function isSilentRenewalFailure(error: unknown): boolean {
  return error instanceof BrowserAuthError && SILENT_RENEWAL_FAILURES.has(error.errorCode);
}
